import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getActiveAddons, getLatestSubscription, getUtilisationLevers, type SubscriptionAddon } from "@/services/apis/subscription";
import { captureCustomSentryException } from "@/utils/sentryHelpers";

// Types for API 1 (latest company subscriptions)
export type Product = {
  id: number;
  productName: string;
};

export type SubscriptionPlan = {
  id: number;
  name: string;
  product: Product;
  billingDays?: number;
};

export type CompanySubscription = {
  id: number;
  created: number;
  updated: number;
  companyId: number;
  subscriptionPlan: SubscriptionPlan;
  targetSubscriptionPlan: unknown | null;
  status: string; // e.g., "TRIAL", "ACTIVE", "TRIAL_ENDED", etc.
  chargebeeCustomerId?: string;
  chargebeeSubscriptionId?: string;
  nextBillingDate?: number;
  cycleStartDate?: number;
  gracePeriod?: number;
  overageBillingStartDate?: number;
  overageBillingEndDate?: number;
  trialStartingNow?: boolean;
};

// Types for API 2 (utilisation levers)
export type UtilisationLevers = {
  percentageOfUsage?: number;
  ruleName?: "NUMBER_OF_CREDITS";
  ruleValue?: string; // e.g., "1" (total credits or limit)
  usageValue?: string; // e.g., "0.0" (used credits)
};

export type NormalizedStatus = "trial" | "trial_ended" | "active" | "unknown";

export type SubscriptionCombined = {
  subscription?: CompanySubscription;
  status: NormalizedStatus;
  productName?: string;
  startDate?: Date;
  nextBillingDate?: Date;
  utilisation?: {
    ruleValue: number;
    usageValue: number;
    percent: number; // 0-100
  };
};

export function useSubscription(companyId: number, currentUser?: any, enable: boolean = false) {
  const queryClient = useQueryClient();
  const hasSessionContext = !!companyId && !!currentUser && enable;

  // Fetch latest subscriptions for the company
  const latestQuery = useQuery<CompanySubscription | undefined, Error>({
    queryKey: ["subscription_latest", companyId],
    enabled: hasSessionContext,
    queryFn: async () => {
      // Get previous data and query state from the cache
      const queryState = queryClient.getQueryState<CompanySubscription>(["subscription_latest", companyId]);
      const prevState = queryState?.data;

      // Fetch new data
      const apiResponse = await getLatestSubscription(companyId);
      const currentState = apiResponse.find((item: any) => item.subscriptionPlan.product.id === 3);
      if (!currentState) {
        return {
          status: 'unknown'
        };
      }

      // Logic to determine if the trial has just started
      let trialHasStarted = false;
      const isTrialActiveNow = currentState.status === 'TRIAL' && !!currentState.nextBillingDate;

      // Only proceed if the current state is an active trial
      if (isTrialActiveNow) {
        // Check if this is a subsequent fetch (not the very first one)
        if (queryState && queryState.dataUpdatedAt > 0) {
          if (!prevState || prevState.status === 'unknown') {
            // Case 1: Previous state was empty, now we have an active trial.
            trialHasStarted = true;
          } else {
            // Case 2: Previous state existed, check if it was an inactive trial.
            const wasTrialInactive = !prevState.nextBillingDate && prevState.status === 'TRIAL';
            if (wasTrialInactive) {
              trialHasStarted = true;
            }
          }
        }
      }

      // Attach the flag to the new data
      currentState.trialStartingNow = trialHasStarted;

      return currentState;
    },
    // Subscription state changes rarely (plan upgrades, trial start). 5 min staleTime
    // avoids hammering the API on every mount; refetchOnWindowFocus ensures the UI
    // reflects changes made in another tab (e.g. after a payment flow).
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Determine the subscription of interest (our product)
  const selectedSubscription = useMemo(() => {
    const list = latestQuery.data || null;
    if (!list) return undefined;
    // Fallback: pick the most recent by created timestamp
    return list;
  }, [latestQuery.data]);

  const companySubscriptionId = selectedSubscription?.id;

  // Fetch utilisation for the selected company subscription
  const utilisationQuery = useQuery<UtilisationLevers, Error>({
    queryKey: ["subscription_utilisation", companySubscriptionId],
    enabled: !!companySubscriptionId && hasSessionContext,
    queryFn: async () => {
      if(companySubscriptionId){
        const data = await getUtilisationLevers(companySubscriptionId);
        let response: UtilisationLevers = {};
        if(Array.isArray(data) && !!data.length){
          // A plan can carry non-usage levers (e.g. the "UseCase" entitlement
          // lever) with no NUMBER_OF_CREDITS entry — `.find` then yields
          // undefined, and a queryFn returning undefined makes react-query
          // throw. Fall back to the empty "no credits configured" shape.
          response = data.find((item: any) => item.ruleName === 'NUMBER_OF_CREDITS') ?? {};
        }
        return response;
      }
      // `refetch()` is exported (SubscriptionContext/UserAvatar call it) and runs
      // even while the query is disabled, so this branch is reachable whenever the
      // company has no IQ subscription id yet. Same empty "no credits configured"
      // shape as above — returning undefined would throw in react-query.
      return {};
    },
    // Same rationale as above: utilisation levers are billing-side data that rarely
    // change mid-session. Window-focus refetch keeps it honest after a plan change.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Active add-ons (FEATURE entitlements like AGENCY_USER_MANAGEMENT_DASHBOARD)
  // for the selected company subscription — same enablement/staleness shape as
  // utilisation above.
  const addonsQuery = useQuery<SubscriptionAddon[], Error>({
    queryKey: ["subscription_addons", companySubscriptionId],
    enabled: !!companySubscriptionId && hasSessionContext,
    queryFn: () => (companySubscriptionId ? getActiveAddons(companySubscriptionId) : Promise.resolve([])),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // True once the add-on answer is actually known (or never will be): either
  // there's no session context yet (nothing has been asked, so nothing to
  // wait for — matches the pre-add-ons behavior of "not loading"), or the
  // real chain has settled: `latestQuery` resolved AND, only if it produced a
  // subscription id, `addonsQuery` resolved too. `addonsQuery.isLoading` alone
  // is `false` while it's simply disabled — which it is for the entire time
  // `latestQuery` is still in flight — so a real entitlement check could
  // report "resolved: not entitled" before the add-ons request has even run.
  const addonsResolved =
    !hasSessionContext || (latestQuery.isFetched && (!companySubscriptionId || addonsQuery.isFetched));

  // A failed fetch here fails CLOSED (hasAddon returns false for everyone,
  // same as "add-on genuinely absent") — same as the codebase's own
  // subscriptionData/creditsData convention. That silence is deliberate for
  // the user, but it must not be silent for us: a real paying customer could
  // otherwise lose an entitled feature during a transient outage with nobody
  // finding out. Same Sentry-capture pattern as the admin memory-settings
  // batch fetch (UserManagement.tsx) — searchable in Sentry by this `feature` tag.
  useEffect(() => {
    if (addonsQuery.error) {
      captureCustomSentryException(addonsQuery.error, undefined, {
        feature: "subscription_addons_fetch",
        companySubscriptionId,
      });
    }
  }, [addonsQuery.error, companySubscriptionId]);

  const hasAddon = useMemo(() => {
    const addons = addonsQuery.data ?? [];
    return (addonName: string) =>
      addons.some((entry) => entry.status === "ACTIVE" && entry.addon?.name === addonName);
  }, [addonsQuery.data]);

  const subscriptionLoading = latestQuery.isFetching || utilisationQuery.isFetching;

  return {
    latestQuery,
    utilisationQuery,
    addonsQuery,
    addonsResolved,
    hasAddon,
    refetchUtilisation: utilisationQuery.refetch,
    subscriptionLoading,
    selectedSubscription,
    subscriptionError: latestQuery.error,
    utilisationError: utilisationQuery.error
  };
}

export type UseSubscriptionReturn = ReturnType<typeof useSubscription>;
